import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  ENRICHABLE_FIELDS,
  HISTORY_PAGE,
  HISTORY_SUGGESTION_STATUSES,
  MODULE_CATALOG,
  resolveFinalValue,
  type HistorySuggestionStatus,
  type ProductSuggestionGroup,
  type SuggestionListItem,
  type ValidationCheck,
} from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useAuth } from '@/app/auth-context';
import { useTenantReady } from '@/app/tenant-context';
import { NoTenantState } from '@/components/no-tenant-state';
import { PaginationButtons } from '@/components/pagination-buttons';
import { ProductThumb } from '@/components/product-thumb';
import { CharacterCounter, DiffPanel } from '@/components/suggestion-diff';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FIELD_LABEL,
  Input,
  QueryState,
  Select,
  Spinner,
} from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { cn, formatNumber, formatRelative } from '@/lib/utils';

const PAGE_SIZE = 25;

type DecisionBucket = 'all' | 'awaiting' | 'rejected' | 'published';

const DECISION_BUCKETS: Array<{
  id: DecisionBucket;
  label: string;
  statuses: HistorySuggestionStatus[];
}> = [
  { id: 'all', label: 'Todas', statuses: [...HISTORY_SUGGESTION_STATUSES] },
  { id: 'awaiting', label: 'Aguardando publicação', statuses: ['approved'] },
  { id: 'rejected', label: 'Recusadas', statuses: ['rejected'] },
  {
    id: 'published',
    label: 'Publicadas / encerradas',
    statuses: ['applied', 'failed', 'superseded'],
  },
];

const STATUS_LABEL: Record<HistorySuggestionStatus, string> = {
  approved: 'aguardando publicação',
  rejected: 'recusada',
  applied: 'publicada na VTEX',
  failed: 'falhou na escrita',
  superseded: 'escrita abortada (conflito)',
};

const STATUS_TONE: Record<
  HistorySuggestionStatus,
  'neutral' | 'success' | 'critical' | 'medium' | 'brand'
> = {
  approved: 'success',
  rejected: 'critical',
  applied: 'brand',
  failed: 'medium',
  superseded: 'neutral',
};

const DECISION_LABEL: Record<string, string> = {
  approve: 'aprovou',
  reject: 'rejeitou',
};

function suggestedValueLabel(status: HistorySuggestionStatus, edited: boolean): string {
  if (edited && (status === 'approved' || status === 'applied')) {
    return 'Valor aprovado (editado)';
  }
  switch (status) {
    case 'rejected':
      return 'Valor rejeitado';
    case 'applied':
      return 'Valor aplicado na VTEX';
    case 'failed':
      return 'Valor que falhou na escrita';
    case 'superseded':
      return 'Valor não publicado (conflito de hash)';
    case 'approved':
      return 'Valor aprovado';
    default:
      return 'Valor sugerido';
  }
}

function decisionSummary(group: ProductSuggestionGroup): string {
  let awaiting = 0;
  let rejected = 0;
  let published = 0;
  for (const item of group.suggestions) {
    if (item.status === 'approved') awaiting += 1;
    else if (item.status === 'rejected') rejected += 1;
    else published += 1;
  }
  const parts: string[] = [];
  if (awaiting > 0) parts.push(`${awaiting} aguardando`);
  if (rejected > 0) parts.push(`${rejected} recusada${rejected === 1 ? '' : 's'}`);
  if (published > 0) parts.push(`${published} encerrada${published === 1 ? '' : 's'}`);
  return parts.join(' · ') || `${group.suggestions.length} sugestões`;
}

/** `applied` (aplicada com sucesso) vem primeiro; o resto mantém a ordem do backend. */
function appliedRank(status: string): number {
  return status === 'applied' ? 0 : 1;
}

function groupAppliedRank(group: ProductSuggestionGroup): number {
  return group.suggestions.some((item) => item.status === 'applied') ? 0 : 1;
}

/**
 * Corpo do histórico de decisões de revisão.
 * Compartilhado por `/review?tab=history` (embedded) e `/history` (standalone).
 */
export function ReviewHistoryPanel({ embedded = false }: { embedded?: boolean }) {
  const { tenantId, isResolving } = useTenantReady();
  const { hasModule } = useAuth();
  const navigate = useNavigate();
  const hasRuns = hasModule('runs');

  const [bucket, setBucket] = useState<DecisionBucket>('all');
  const [field, setField] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);

  const statusFilter = useMemo(
    () => DECISION_BUCKETS.find((item) => item.id === bucket)?.statuses ?? [...HISTORY_SUGGESTION_STATUSES],
    [bucket],
  );

  const statsQuery = useQuery({
    queryKey: ['suggestions', tenantId, 'stats'],
    queryFn: () => api.suggestions.stats(tenantId as string),
    enabled: tenantId !== null,
  });

  const query = useQuery({
    queryKey: ['suggestions', tenantId, 'history', statusFilter, field, search, page],
    queryFn: () =>
      api.suggestions.listGrouped(tenantId as string, {
        status: statusFilter,
        field: field || undefined,
        search: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: tenantId !== null,
  });

  // Sempre trazer o que foi aplicado com sucesso para o topo: sugestões
  // `applied` primeiro dentro de cada produto e produtos com aplicações antes
  // dos demais. Ordenação estável preserva o critério do backend nos empates.
  const groups = useMemo<ProductSuggestionGroup[]>(() => {
    const raw = query.data?.items ?? [];
    return raw
      .map((group) => ({
        ...group,
        suggestions: [...group.suggestions].sort(
          (a, b) => appliedRank(a.status) - appliedRank(b.status),
        ),
      }))
      .sort((a, b) => groupAppliedRank(a) - groupAppliedRank(b));
  }, [query.data]);

  const preferredGroup = groups.find((group) => group.productId === selectedProductId) ?? null;
  const activeGroup = preferredGroup ?? groups[0] ?? null;
  const mobileDetailOpen = selectedProductId !== null && preferredGroup !== null;

  const preferredSuggestion =
    activeGroup?.suggestions.find((item) => item.id === selectedSuggestionId) ?? null;
  const activeSuggestion = preferredSuggestion ?? activeGroup?.suggestions[0] ?? null;

  /** Qualquer mudança de filtro volta à primeira página e solta a seleção. */
  function resetBrowsing(): void {
    setPage(1);
    setSelectedProductId(null);
    setSelectedSuggestionId(null);
  }

  function applySearch(): void {
    setSearch(searchDraft.trim());
    resetBrowsing();
  }

  function clearSearch(): void {
    setSearchDraft('');
    if (!search) return;
    setSearch('');
    resetBrowsing();
  }

  if (isResolving) {
    return <Spinner label="Carregando conta…" />;
  }

  if (!tenantId) {
    return <NoTenantState />;
  }

  const productTotal = query.data?.total ?? 0;
  const suggestionTotal = query.data?.suggestionTotal ?? 0;
  const pageCount = Math.max(1, Math.ceil(productTotal / PAGE_SIZE));
  const byStatus = statsQuery.data?.byStatus ?? {};
  const awaitingCount = byStatus.approved ?? 0;
  const rejectedCount = byStatus.rejected ?? 0;
  const publishedCount =
    (byStatus.applied ?? 0) + (byStatus.failed ?? 0) + (byStatus.superseded ?? 0);

  const headerDescription =
    productTotal === 0
      ? 'Nenhuma decisão de revisão com os filtros atuais.'
      : `Decisões de revisão · ${formatNumber(awaitingCount)} aguardando publicação · ${formatNumber(rejectedCount)} recusadas · ${formatNumber(publishedCount)} publicadas/encerradas · ${formatNumber(productTotal)} produtos nesta vista (${formatNumber(suggestionTotal)} sugestões)`;

  const filterActions = (
    <>
      <div className="flex w-full gap-2 sm:w-auto">
        <Select
          className="min-w-0 flex-1 sm:flex-none"
          aria-label="Filtrar por campo"
          value={field}
          onChange={(event) => {
            setField(event.target.value);
            resetBrowsing();
          }}
        >
          <option value="">Todos os campos</option>
          {ENRICHABLE_FIELDS.map((item) => (
            <option key={item} value={item}>
              {FIELD_LABEL[item] ?? item}
            </option>
          ))}
        </Select>
      </div>
      <form
        className="flex w-full items-center gap-2 sm:w-auto"
        onSubmit={(event) => {
          event.preventDefault();
          applySearch();
        }}
      >
        <div className="relative min-w-0 flex-1 sm:w-56 sm:flex-none">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
            aria-hidden
          />
          <Input
            type="search"
            className="pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
            placeholder="Buscar produto ou texto…"
            aria-label="Buscar no histórico"
            autoComplete="off"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
          {searchDraft ? (
            <button
              type="button"
              aria-label="Limpar busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-full p-1 text-[var(--color-muted)] transition hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]"
              onClick={clearSearch}
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>
        <Button type="submit" variant="secondary">
          Buscar
        </Button>
      </form>
    </>
  );

  const listHeightClass = embedded
    ? 'h-[calc(100dvh-12.5rem)] md:h-[calc(100dvh-9.5rem)]'
    : 'h-[calc(100dvh-10.5rem)] md:h-[calc(100dvh-7.5rem)]';

  return (
    <>
      {!embedded ? (
        <PageHeader
          title={HISTORY_PAGE.label}
          description={headerDescription}
          actions={filterActions}
        />
      ) : (
        <div className="flex w-full flex-wrap items-center gap-2 border-b border-[var(--color-hairline)] px-4 py-3 sm:px-6">
          <p className="mr-auto min-w-0 flex-1 text-sm text-[var(--color-muted)] sm:flex-none sm:basis-full lg:basis-auto">
            {headerDescription}
          </p>
          {filterActions}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 border-b border-[var(--color-hairline)] px-4 py-2 sm:px-6">
        {DECISION_BUCKETS.map((item) => {
          const count =
            item.id === 'awaiting'
              ? awaitingCount
              : item.id === 'rejected'
                ? rejectedCount
                : item.id === 'published'
                  ? publishedCount
                  : awaitingCount + rejectedCount + publishedCount;
          const active = bucket === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setBucket(item.id);
                resetBrowsing();
              }}
              className={cn(
                'cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition',
                active
                  ? 'border-[var(--color-primary)] bg-[color-mix(in_srgb,var(--color-primary)_14%,transparent)] text-[var(--color-ink)]'
                  : 'border-[var(--color-hairline)] bg-[var(--color-surface-card)] text-[var(--color-muted)] hover:text-[var(--color-ink)]',
              )}
            >
              {item.label}
              <span className="ml-1.5 tabular-nums text-[var(--color-muted)]">
                {formatNumber(count)}
              </span>
            </button>
          );
        })}
        {bucket === 'awaiting' && awaitingCount > 0 && hasRuns ? (
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => navigate(MODULE_CATALOG.runs.path)}
          >
            Escrever na VTEX
          </Button>
        ) : null}
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        loadingLabel="Carregando histórico…"
        errorTitle="Não foi possível carregar o histórico"
        onRetry={() => void query.refetch()}
      >
        {groups.length === 0 ? (
          <EmptyState
            title="Histórico vazio"
            description={
              embedded
                ? `Aprove ou rejeite sugestões em Pendentes para começar a registrar o histórico.`
                : `Aprove ou rejeite sugestões em ${MODULE_CATALOG.review.label} para começar a registrar o histórico.`
            }
            action={
              embedded ? undefined : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(MODULE_CATALOG.review.path)}
                >
                  Ir para {MODULE_CATALOG.review.label}
                </Button>
              )
            }
          />
        ) : (
          <div
            className={cn(
              'flex flex-col overflow-hidden md:grid md:grid-cols-[min(340px,38vw)_1fr]',
              listHeightClass,
            )}
          >
            <aside
              className={cn(
                'flex min-h-0 flex-col overflow-hidden border-b border-[var(--color-hairline)] bg-[var(--color-surface-soft)] md:border-b-0 md:border-r',
                mobileDetailOpen ? 'hidden md:flex' : 'flex',
              )}
            >
              <div className="min-h-0 flex-1 divide-y divide-[var(--color-hairline)] overflow-y-auto">
                {groups.map((group) => (
                  <HistoryProductRow
                    key={group.productId}
                    group={group}
                    isActive={group.productId === activeGroup?.productId}
                    onSelect={() => {
                      setSelectedProductId(group.productId);
                      setSelectedSuggestionId(group.suggestions[0]?.id ?? null);
                    }}
                  />
                ))}
              </div>

              <PaginationButtons
                className="justify-between border-t border-[var(--color-hairline)] px-3 py-2"
                page={page}
                pageCount={pageCount}
                onPrev={() => setPage((current) => current - 1)}
                onNext={() => setPage((current) => current + 1)}
              />
            </aside>

            <section
              className={cn(
                'min-h-0 flex-1 overflow-y-auto bg-[var(--color-canvas)] p-4 sm:p-6',
                mobileDetailOpen ? 'block' : 'hidden md:block',
              )}
            >
              {activeGroup && activeSuggestion ? (
                <div className="w-full">
                  <button
                    type="button"
                    className="mb-3 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-[var(--color-primary)] md:hidden"
                    onClick={() => setSelectedProductId(null)}
                  >
                    <ChevronLeft className="size-3.5" />
                    Voltar à lista
                  </button>
                  <HistoryProductDetail
                    group={activeGroup}
                    activeSuggestion={activeSuggestion}
                    onSelectSuggestion={(id) => {
                      setSelectedProductId(activeGroup.productId);
                      setSelectedSuggestionId(id);
                    }}
                    onWriteClick={
                      hasRuns &&
                      (bucket === 'awaiting' ||
                        activeGroup.suggestions.some((item) => item.status === 'approved'))
                        ? () => navigate(MODULE_CATALOG.runs.path)
                        : undefined
                    }
                  />
                </div>
              ) : (
                <EmptyState title="Selecione um produto" />
              )}
            </section>
          </div>
        )}
      </QueryState>
    </>
  );
}

const QUEUE_TAGS_COLLAPSED_CLASS = 'max-h-[3rem] overflow-hidden';

function HistoryProductRow({
  group,
  isActive,
  onSelect,
}: {
  group: ProductSuggestionGroup;
  isActive: boolean;
  onSelect(): void;
}) {
  const suggestionKey = group.suggestions.map((item) => item.id).join(',');
  const tagsRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useLayoutEffect(() => {
    setExpanded(false);
    setCanExpand(false);
  }, [suggestionKey]);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = tagsRef.current;
    if (!el) return;

    const measure = (): void => {
      setCanExpand(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, suggestionKey]);

  return (
    <div
      className={cn(
        'flex cursor-pointer items-start gap-2 px-3 py-2.5 transition',
        isActive
          ? 'bg-[var(--color-surface-card)]'
          : 'hover:bg-[var(--color-surface-elevated)]',
      )}
      onClick={onSelect}
    >
      <ProductThumb src={group.imageUrl} alt={group.productName} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-[var(--color-ink)]">{group.productName}</p>
        <p className="truncate text-[11px] text-[var(--color-muted)]">
          {group.categoryName ?? 'sem categoria'} · #{group.productId} · {decisionSummary(group)}
        </p>
        <div
          ref={tagsRef}
          className={cn(
            'mt-1 flex flex-wrap items-center gap-1',
            expanded ? null : QUEUE_TAGS_COLLAPSED_CLASS,
          )}
        >
          {group.suggestions.map((item) => (
            <Badge
              key={item.id}
              tone={STATUS_TONE[item.status as HistorySuggestionStatus] ?? 'neutral'}
            >
              {FIELD_LABEL[item.field] ?? item.field}
              {' · '}
              {STATUS_LABEL[item.status as HistorySuggestionStatus] ?? item.status}
            </Badge>
          ))}
        </div>
        {canExpand ? (
          <button
            type="button"
            aria-expanded={expanded}
            className="mt-1 cursor-pointer text-[11px] font-medium text-[var(--color-primary)] hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((current) => !current);
            }}
          >
            {expanded ? 'Ver menos' : 'Ver mais'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function HistoryProductDetail({
  group,
  activeSuggestion,
  onSelectSuggestion,
  onWriteClick,
}: {
  group: ProductSuggestionGroup;
  activeSuggestion: SuggestionListItem;
  onSelectSuggestion(id: string): void;
  onWriteClick?: () => void;
}) {
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-start gap-3">
        <ProductThumb src={group.imageUrl} alt={group.productName} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-[var(--color-ink)]">{group.productName}</h2>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Produto #{group.productId} · {group.categoryName ?? 'sem categoria'} ·{' '}
                {group.brandName ?? 'sem marca'} · {decisionSummary(group)}
              </p>
            </div>
            {onWriteClick ? (
              <Button variant="secondary" size="sm" onClick={onWriteClick}>
                Escrever na VTEX
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {group.suggestions.map((item) => {
          const isActive = item.id === activeSuggestion.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelectSuggestion(item.id)}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border px-2.5 py-1.5 text-[11px] font-medium transition',
                isActive
                  ? 'border-[var(--color-primary)] bg-[color-mix(in_srgb,var(--color-primary)_12%,transparent)] text-[var(--color-ink)]'
                  : 'border-[var(--color-hairline)] bg-[var(--color-surface-card)] text-[var(--color-muted)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
              )}
            >
              {FIELD_LABEL[item.field] ?? item.field}
              <Badge tone={STATUS_TONE[item.status as HistorySuggestionStatus] ?? 'neutral'}>
                {STATUS_LABEL[item.status as HistorySuggestionStatus] ?? item.status}
              </Badge>
            </button>
          );
        })}
      </div>

      <HistorySuggestionDetail key={activeSuggestion.id} suggestion={activeSuggestion} />
    </div>
  );
}

function HistorySuggestionDetail({ suggestion }: { suggestion: SuggestionListItem }) {
  const finalValue = resolveFinalValue(suggestion);
  const current = suggestion.currentValue ?? '';
  const status = suggestion.status as HistorySuggestionStatus;
  const review = suggestion.latestReview;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="brand">{FIELD_LABEL[suggestion.field] ?? suggestion.field}</Badge>
          <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>
          {suggestion.skuId !== null ? <Badge tone="neutral">SKU {suggestion.skuId}</Badge> : null}
          {suggestion.fileId != null ? (
            <Badge tone="neutral">arquivo {suggestion.fileId}</Badge>
          ) : null}
          {suggestion.editedValue ? <Badge tone="medium">editada na revisão</Badge> : null}
        </div>
        {suggestion.imageUrl ? (
          <div className="mt-3 overflow-hidden rounded-2xl border border-[var(--color-hairline)] bg-[var(--color-surface-elevated)]">
            <img
              src={suggestion.imageUrl}
              alt={suggestion.currentValue ?? 'Imagem do SKU'}
              className="mx-auto max-h-48 object-contain"
            />
          </div>
        ) : null}
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Gerada {formatRelative(suggestion.createdAt)}
          {suggestion.usage
            ? ` · ${suggestion.usage.model} (prompt ${suggestion.usage.promptVersion})`
            : ''}
        </p>
        {review ? (
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            {review.userName} {DECISION_LABEL[review.decision] ?? review.decision}{' '}
            {formatRelative(review.createdAt)}
            {review.note ? ` · “${review.note}”` : ''}
          </p>
        ) : null}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="flex flex-col">
          <div className="flex items-center justify-between border-b border-[var(--color-hairline)] px-4 py-2">
            <span
              className="text-xs font-medium text-[var(--color-muted)]"
              title="Baseline do snapshot na geração — não é leitura ao vivo da VTEX"
            >
              Valor na geração
            </span>
            <CharacterCounter field={suggestion.field} value={current} />
          </div>
          <div className="flex-1 px-4 py-3">
            <DiffPanel
              before={current}
              after={finalValue}
              side="before"
              field={suggestion.field}
            />
          </div>
        </Card>

        <Card className="flex flex-col">
          <div className="flex items-center justify-between border-b border-[var(--color-hairline)] px-4 py-2">
            <span className="text-xs font-medium text-[var(--color-muted)]">
              {suggestedValueLabel(status, Boolean(suggestion.editedValue))}
            </span>
            <CharacterCounter field={suggestion.field} value={finalValue} />
          </div>
          <div className="flex-1 px-4 py-3">
            <DiffPanel
              before={current}
              after={finalValue}
              side="after"
              field={suggestion.field}
            />
          </div>
        </Card>
      </div>

      {suggestion.rationale ? (
        <Card>
          <h3 className="border-b border-[var(--color-hairline)] px-4 py-2 text-xs font-medium text-[var(--color-muted)]">
            Justificativa do modelo
          </h3>
          <p className="px-4 py-3 text-sm leading-relaxed text-[var(--color-ink)]">
            {suggestion.rationale}
          </p>
        </Card>
      ) : null}

      {suggestion.validationReport.checks.length > 0 ? (
        <Card>
          <div className="border-b border-[var(--color-hairline)] px-4 py-2 text-xs font-medium text-[var(--color-muted)]">
            Checagens determinísticas
          </div>
          <div className="divide-y divide-[var(--color-hairline)]">
            {suggestion.validationReport.checks.map((check) => (
              <CheckRow key={check.id} check={check} />
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function CheckRow({ check }: { check: ValidationCheck }) {
  const tone = check.passed ? 'success' : check.level === 'blocking' ? 'critical' : 'medium';
  const label = check.passed ? 'ok' : check.level === 'blocking' ? 'bloqueia' : 'aviso';
  return (
    <div className="flex items-start gap-3 px-4 py-2">
      <Badge tone={tone} className="mt-0.5 shrink-0">
        {label}
      </Badge>
      <div className="min-w-0">
        <p className="text-xs font-medium text-[var(--color-ink)]">{check.label}</p>
        {check.message ? (
          <p className="text-[11px] text-[var(--color-muted)]">{check.message}</p>
        ) : null}
      </div>
    </div>
  );
}
